import React from 'react'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'

/**
 * DefaultLoading Component - Customizable loading spinner with text
 *
 * Displays a centered loading spinner with optional text and customizable appearance.
 * Flexible component for use in various contexts where loading state needs indication.
 *
 * Features:
 * - Configurable icon size (sm, md, lg, xl)
 * - Optional "Loading..." text
 * - Customizable text color
 * - Additional className support for positioning
 * - Spinning animation
 * - Flex centered layout
 *
 * Size Options:
 * - sm: h-4 w-4 (16px)
 * - md: h-5 w-5 (20px) - default
 * - lg: h-6 w-6 (24px)
 * - xl: h-8 w-8 (32px)
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.size='md'] - Icon size (sm, md, lg, xl)
 * @param {boolean} [props.showText=true] - Show "Loading..." text
 * @param {string} [props.textColor='text-gray-600'] - Tailwind color classes for text/icon
 * @param {string} [props.className=''] - Additional Tailwind classes for wrapper
 * @returns {JSX.Element} Loading spinner component
 *
 * @remarks
 * - Uses flex column layout for vertical stacking
 * - Space between spinner and text: space-y-2
 * - Text size: text-sm with font-medium
 * - textColor applied to both icon and text
 * - Icon from react-icons (AiOutlineLoading3Quarters)
 * - className prop useful for positioning (absolute, fixed, etc.)
 *
 * @example
 * <DefaultLoading size="lg" showText={true} textColor="text-blue-600" />
 *
 * @example
 * <DefaultLoading size="sm" showText={false} className="absolute inset-center" />
 */
const DefaultLoading = ({
  size = 'md',
  showText = true,
  textColor = 'text-gray-600',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8'
  }

  return (
    <div className={`flex flex-col items-center justify-center space-y-2 ${className}`}>
      <AiOutlineLoading3Quarters
        className={`${sizeClasses[size]} ${textColor} animate-spin`}
      />
      {showText && (
        <span className={`text-sm font-medium ${textColor}`}>
          Loading...
        </span>
      )}
    </div>
  )
}

export default DefaultLoading